import React, { useEffect } from 'react';
import { Upload } from 'lucide-react';
import { UseFormRegister, UseFormSetValue, FieldError, UseFormTrigger } from 'react-hook-form';
import { FlavourFormData } from '../../types';
import toast from 'react-hot-toast';

interface AppIconUploaderProps {
  register: UseFormRegister<any>;
  preview: string | null;
  onPreviewChange: (preview: string | null) => void;
  setValue: UseFormSetValue<any>;
  trigger?: UseFormTrigger<any>;
  inputId: string;
  isRequired?: boolean;
  error?: FieldError | string;
  label?: string;
  description?: string;
  fieldName?: string;
}

const AppIconUploader: React.FC<AppIconUploaderProps> = ({
  register,
  preview,
  onPreviewChange,
  setValue,
  trigger,
  inputId,
  isRequired = false,
  error,
  label = "App Icon",
  description = "PNG, JPG up to 5MB. Required ratio 1:1",
  fieldName = "appIcon",
}) => {
  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(`${label} must be less than 5MB`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        onPreviewChange(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setValue(fieldName, file, { shouldValidate: true });
      
      // Trigger validation after setting the value
      if (trigger) {
        setTimeout(() => {
          trigger(fieldName);
        }, 100);
      }
    }
  };

  // Trigger validation when preview changes
  useEffect(() => {
    if (trigger && preview) {
      trigger(fieldName);
    }
  }, [preview, trigger, fieldName]);

  // Get the error message
  const errorMessage = typeof error === 'string' ? error : error?.message;

  return (
    <div>
      <label className={`block text-sm font-medium mb-1 ${errorMessage ? 'text-red-600' : 'text-gray-700'}`}>
        {label} {isRequired && '*'}
      </label>
      <div className="flex items-center space-x-4">
        {preview && (
          <img 
            src={preview} 
            alt={`${label.toLowerCase()} preview`} 
            className="w-16 h-16 rounded-lg object-cover border"
          />
        )}
        <div className="flex-1">
          <input
            {...register(fieldName, { 
              required: false, // We'll handle required validation manually
              validate: (value) => {
                // For required fields, check if we have either a file or a preview
                if (isRequired) {
                  const hasFile = value && value instanceof File;
                  const hasPreview = preview && preview.trim() !== '';
                  
                  if (!hasFile && !hasPreview) {
                    return `${label} is required`;
                  }
                }
                return true;
              }
            })}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
            id={inputId}
          />
          <label
            htmlFor={inputId}
            className={`cursor-pointer inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
              errorMessage 
                ? 'border-red-300 text-red-700 focus:ring-red-500' 
                : 'border-gray-300 text-gray-700'
            }`}
          >
            <Upload className="h-4 w-4 mr-2" />
            {preview ? `Change ${label}` : `Upload ${label}`}
          </label>
          <p className="text-xs text-gray-500 mt-1">{description}</p>
          {errorMessage && (
            <p className="text-xs text-red-600 mt-1">{errorMessage}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppIconUploader; 